import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { ProgressBar } from "@/components/ProgressBar";
import { classAccent } from "@/lib/brand";
import { ArrowRight, Users } from "lucide-react";

export default function MentorHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet("/mentor/students")
      .then(setStudents)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, []);

  const mentorClasses = user?.mentor_classes || [];
  const nav = [{ to: "/mentor", label: "My Students" }];

  return (
    <AppShell nav={nav}>
      <div className="animate-rise">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {user?.organization_name}
        </p>
        <h1 className="text-4xl font-extrabold mb-2">My Students</h1>
        <p className="text-muted-foreground mb-7">
          {mentorClasses.length > 0
            ? "You can view answers and progress for the classes you're approved to mentor."
            : "You can view answers and progress for all your assigned students."}
        </p>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[0, 1].map((i) => <div key={i} className="h-40 rounded-3xl border-2 border-ink bg-white/60 animate-pulse" />)}
          </div>
        ) : students.length === 0 ? (
          <div className="rounded-3xl border-2 border-ink bg-white p-10 text-center" data-testid="no-students">
            <Users size={32} className="mx-auto mb-3 text-muted-foreground" />
            <p className="font-semibold">No students assigned yet.</p>
            <p className="text-sm text-muted-foreground">Students who choose you as their mentor will appear here.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {students.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/mentor/students/${s.id}`)}
                data-testid={`student-card-${s.id}`}
                className="text-left rounded-3xl border-2 border-ink bg-white shadow-pop press p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xl font-extrabold">{s.name}</div>
                    <div className="text-sm text-muted-foreground">{s.email}</div>
                  </div>
                  <ArrowRight size={18} />
                </div>
                <div className="space-y-3">
                  {(s.class_progress || []).map((c, i) => (
                    <div key={c.id || c.slug}>
                      <div className="flex justify-between text-xs font-bold mb-1">
                        <span>{c.title}</span>
                        <span>{c.progress}%</span>
                      </div>
                      <ProgressBar value={c.progress} color={classAccent(c.slug, i, c.color).bg} height={8} />
                    </div>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
